/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch } from 'react-redux';

import { colors, fonts } from '@blend/consumer-components';

import { addNotifications } from '../../../actions/pageActions';
import { notaryAPI } from '../../../api/notary';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { NotificationItem } from '../../common/Notifications/NotificationItem';
import SigningDocument from '../../common/Documents/SigningDocument';
import { ConfirmationModal } from '../../common/Modals/ConfirmationModal/ConfirmationModal';

export namespace VoidDocumentConfirmationModal {
  export interface Props {
    isModalOpen: boolean;
    handleModalClose: () => void;
    document: SigningDocument;
  }
}

const styles = StyleSheet.create({
  text: {
    fontSize: 16,
    lineHeight: '22px',
    ...fonts.UntitledSans,
  },
  bold: {
    fontWeight: fonts.weight.bold,
  },
});

export const VoidDocumentConfirmationModal: React.FC<VoidDocumentConfirmationModal.Props> = ({
  isModalOpen,
  handleModalClose,
  document,
}) => {
  const dispatch = useDispatch();
  const { callDetails } = useRootSelector(state => ({ callDetails: state.callDetails }));
  const { id, name } = document;

  const voidDocument = async () => {
    handleModalClose();
    try {
      await notaryAPI.documents.voidDocument(callDetails!.id, id);
    } catch (err) {
      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'error',
          iconType: 'error',
          content: (err as Error).message,
          shouldAutoRemove: true,
        },
      ];
      addNotifications(notifications)(dispatch);
    }
  };

  return (
    <ConfirmationModal
      isOpen={isModalOpen}
      contentLabel={'Void Document'}
      heading={'Do you want to void this document?'}
      confirmButtonText={'Yes'}
      handleConfirm={voidDocument}
      handleClose={handleModalClose}
      closeButtonText={'Cancel'}
      buttonColor={colors.reflex}
      data-it="VoidDocumentConfirmationModal"
    >
      <div className={css(styles.text)}>
        If you say yes, <strong className={css(styles.bold)}>{name}</strong> will immediately be marked void. Documents
        should only be marked void after consulting with the settlement agent.
        <br />
        <strong className={css(styles.bold)}>This action cannot be undone.</strong>
      </div>
    </ConfirmationModal>
  );
};
